'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { tv } from 'tailwind-variants';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import { Image } from '@/app_components/common/Image';
import { useAdTag } from '@/app_lib/hooks/useAdTag';
import { useAdContext } from '@/app_lib/providers/AdProvider';
import { cn } from '@/app_lib/utils/utils';
import { placeholderFortuneImage } from '@/appconstants';

const videoStyles = tv({
  slots: {
    close: '',
    container: '',
    iframe: '',
  },
  variants: {
    dockingMode: {
      false: {
        close: 'hidden',
        container: 'w-full h-full aspect-video',
        iframe: 'w-full h-full aspect-video',
      },
      true: {
        close: 'absolute top-0 right-0 w-5 z-[10001] text-primary-inverse',
        container: 'w-[300px] h-[169px] fixed bottom-[70px] right-4 z-header-popover',
        iframe: 'w-[300px] h-[169px] fixed bottom-[70px] right-4 z-header-popover',
      },
    },
  },
});

export interface VodVideoProps {
  videoId: string;
  className?: string;
  // currently not being used, but it seems to be used for rankings. Reconsider if needed
  dataCy?: string;
  videoPosterCy?: string;
  autoplay?: boolean;
  docking?: boolean;
  loading?: React.ReactNode;
}

/** @deprecated Don't use for anything new besides legacy reasons */
export const VodVideo = ({
  videoId,
  className = '',
  dataCy = '',
  videoPosterCy = '',
  autoplay = true,
  docking = false,
  loading = null,
}: VodVideoProps) => {
  const pathname = usePathname();
  const { pageAdConfig } = useAdContext();
  const adTag = useAdTag(pathname, pageAdConfig);

  const [play, setPlay] = useState(false);
  const [dockingMode, setDockingMode] = useState(false);
  const [disableDockingMode, setDisableDockingMode] = useState(false);
  const [videoContainerRef, inViewPort] = useInView({ threshold: 0.5 });

  const videoUrl = `https://fortune.com/videos/embed/${videoId}?autoplay=${autoplay}&adTagUrl=${encodeURIComponent(adTag)}`;

  const loadingContent = loading ?? (
    <Image
      alt='Video Poster'
      className='w-full h-full aspect-video'
      data-cy={videoPosterCy}
      height={450}
      priority
      src={`${placeholderFortuneImage}?resize=800,450`}
      width={800}
    />
  );

  useEffect(() => {
    if (!(play || disableDockingMode)) {
      const timer = setTimeout(() => {
        if (inViewPort) {
          setPlay(true);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (play && !inViewPort && docking) {
      setDockingMode(true);
    }

    if (play && inViewPort && docking) {
      setDockingMode(false);
    }
    return () => {};
  }, [disableDockingMode, inViewPort, play, docking]);

  return (
    <div
      className={cn('relative block w-full aspect-video', className)}
      ref={videoContainerRef}
    >
      <div
        className={videoStyles().container({ dockingMode })}
        data-cy={dataCy}
      >
        <ActionableIcon
          className={videoStyles().close({ dockingMode })}
          onClick={() => {
            if (docking) {
              setDockingMode(false);
              setDisableDockingMode(true);
            }
            setPlay(false);
          }}
        >
          <Icon icon='X' />
        </ActionableIcon>
        {play ? (
          <iframe
            {...(autoplay && { allow: 'autoplay' })}
            allowFullScreen
            aria-label='Company overview video'
            className={videoStyles().iframe({ dockingMode })}
            frameBorder='0'
            scrolling='no'
            src={videoUrl}
            title='vod-video'
          />
        ) : (
          loadingContent
        )}
      </div>
    </div>
  );
};
